import React from "react";
import {SummaryColumn, Paragraph, ItalicText, SubHeaderText} from "../components/BuildingBlocks/BuildingBlocks"
import {ExternalLink} from "../../../shared-components/RoutingLinks/RoutingLinks";
import Code from "../components/Code/Code";
import CollapsibleArea from "../components/CollapsibleArea/CollapsibleArea";

const MapMateIntro = () => {
    return <SummaryColumn>
            <Paragraph>
                Lately, we presented you with our new HTTP framework - HttpMate. In the<ExternalLink
                to={"/httpmate-intro"} displayText={"introduction article"}/>, we referred
                to the mapping
                of requests and responses to domain objects as "the most complicated technical detail", and how
                another
                mate - MapMate,
                is there to help us out.
            </Paragraph>
            <Paragraph>Indeed, MapMate takes the load off of HttpMate when it comes to mapping the
                request attributes to your domain objects. It takes care of converting the responses to the appropriate
                format (JSON, XML, YAML, etc), essentially performing deserialization and serialization, but also so
                much more.
            </Paragraph>
            <Paragraph>In this article, we'll focus on how MapMate helps us deal with (de)serializing the
                request/response objects in a controlled and predictable manner.
            </Paragraph>


            <SubHeaderText>Custom Primitives</SubHeaderText>
            <Paragraph>
                Let's recap our example from the previous article; we have a simple UseCase of sending an
                email. For that we need an Email object, that would have:
            </Paragraph>

            <ul>
                <li>Sender</li>
                <li>Receiver</li>
                <li>Subject</li>
                <li>Body</li>
            </ul>

            <Paragraph>
                All of these fields can be represented as Strings, or even as a byte array. The more generic type
                you choose to represent your data with, the more possibilities you have interpreting it later on.
                Imagine the following method:
            </Paragraph>
            <Code language={"java"}>
                {`
                            public Object sendEmail(final Object sender, final Object receiver, final Object subject, final Object body) {
                                    ...
                            }
                        `}
            </Code>
            <Paragraph>
                This leaves us with a lot of open questions:

                <ul>
                    <li>is sender instanceOf String or byte[]?</li>
                    <li>what is the encoding?</li>
                    <li>is the body zip compressed?</li>
                    ...
                </ul>
                The list goes on.
                While there are cases where this might be appropriate, I bet you would feel more comfortable with:
            </Paragraph>
            <Code language={"java"}>
                {`
                            public String sendEmail(final String sender, final String receiver, final String subject, final String body) {
                                    ...
                            }
                        `}
            </Code>
            <Paragraph>
                The latter leaves less room for interpretation: for instance, we no longer have to assume an
                encoding
                or question the type of the parameter altogether.
            </Paragraph>
            <Paragraph>
                It's still ambiguous though, is the sender field carrying the name of the user or her email address?
                This same ambiguity is the reason of endless uncertainty when writing unit tests... to the degree of
                using random string generators to test a method one knows must accept only email addresses.
            </Paragraph>
            <Paragraph>
                The following method signature does way better in terms of ambiguity, both for humans and compilers:
            </Paragraph>
            <Code language={"java"}>
                {`
                            public Receipt sendEmail(final EmailAddress sender, final EmailAddress receiver, final Subject subject, final Body body) {
                                    ...
                            }
                        `}
            </Code>
            <Paragraph>
                The same way we can trust that String is a String and Integer is an Integer, we can now trust that
                the EmailAddress is an email address, and a Subject is actually a subject - they became custom
                primitives
                to the send email method.
            </Paragraph>
            <Paragraph>
                Sender and Receiver are not faceless
                "Strings", and they are very much different from "Subject" and "Body". They are email addresses, and we
                can
                represent them as such by validating their value using, for example, some sane Regular Expressions.
                (beware of<ExternalLink to={"https://en.wikipedia.org/wiki/ReDoS"} displayText={"ReDoS"}/>)
            </Paragraph>
            <Paragraph>
                The rationality of using factory methods as a means of creating "always valid" objects has been
                widely
                discussed and
                validated. Having that in mind, we'd create an EmailAddress class for our example use case that
                would
                then
                be used as a custom primitive type for Sender and Receiver fields.
            </Paragraph>

            <Code language={"java"}>
                {`
                public final class EmailAddress {
                    private final String value;

                    private EmailAddress(final String value) {
                        this.value = value;
                    }

                    public static EmailAddress fromStringValue(final String value) {
                        final String validated = EmailAddressValidator.ensureEmailAddress(value, "emailAddress");
                        return new EmailAddress(validated);
                    }
                }
                `}
            </Code>
            <Paragraph>
                Since - the only instance variable is private and final, it can only be assigned using the private
                constructor,
                which can only be invoked from outside of the class using the public factory method which validates
                the
                input, before passing it to the constructor - we can be sure whenever we receive an instance of
                EmailAddress it is valid.
            </Paragraph>
            <Paragraph>
                If you are curious at this point about the EmailAddressValidator implementation, be sure to checkout
                the<ExternalLink
                to={"https://github.com/envimate/email-use-case-example/blob/master/src/main/java/com/envimate/examples/email_use_case/validation/EmailAddressValidator.java"}
                displayText={"source code"}/> of this example project.
            </Paragraph>
            <Paragraph>
                Now our domain objects can use not only the default primitives such as String, Double, Integer, etc
                but
                also Custom Primitives like EmailAddress and Body, Subject, etc. Typically though we'd need to be
                able to
                store a domain object in a database or communicate it to another service or UI. No other party
                though
                knows about a Custom Primitive called EmailAddress. Hence we need a "representation" of it,
                something
                both HTTP, persistence and human-friendly - a String.
            </Paragraph>
            <Code language={"java"}>
                {`
                        public final class EmailAddress {
                            private final String value;

                            public static EmailAddress fromStringValue(final String value) {
                                final String validated = EmailAddressValidator.ensureEmailAddress(value, "emailAddress");
                                return new EmailAddress(validated);
                            }

                            public String stringValue() {
                                return this.value;
                            }
                        }
                    `}
            </Code>
            <Paragraph>
                The method "stringValue" we added is the String representation of our Custom Primitive. Now we can send
                the
                "stringValue" of the EmailAddress and then reconstruct it based on the value received.
                In essence, the "fromString" and "stringValue" methods are the "deserialization" and
                "serialization" mechanisms of the EmailAddress, respectively.
            </Paragraph>
            <Paragraph>
                Following this approach, we can create Custom Primitives for the Body and Subject of our Email as
                well:
            </Paragraph>
            <Code language={"java"}>
                {`
                        public final class Body {
                            private final String value;

                            public static Body fromStringValue(final String value) {
                                final String emailAddress = LengthValidator.ensureLength(value, 1, 1000, "body");
                                return new Body(emailAddress);
                            }

                            public String stringValue() {
                                return this.value;
                            }
                        }
                        `}
                {`
                        public final class Subject {
                            private final String value;

                            public static Subject fromStringValue(final String value) {
                                final String validated = LengthValidator.ensureLength(value, 1, 256, "subject");
                                return new Subject(validated);
                            }

                            public String stringValue() {
                                return this.value;
                            }
                        }
                    `}
            </Code>

            <SubHeaderText>Data Transfer Objects</SubHeaderText>

            <Paragraph>
                Armed with our Custom Primitives, we are now ready to create the proper Data Transfer Object - the
                Email,
                which is a pretty straight forward task, since it's basically an immutable struct:
            </Paragraph>

            <Code language={"java"}>
                {`
                    public final class Email {
                        public final EmailAddress sender;
                        public final EmailAddress receiver;
                        public final Subject subject;
                        public final Body body;
                    }
                    `}
            </Code>
            <Paragraph>
                The same "always valid" approach applies to the Data Transfer Objects as well, except here we have
                an
                easier time, as we are leveraging our Custom Primitives.
            </Paragraph>
            <Paragraph>
                The factory method of the DTO can be as simple as validating the presence of the mandatory fields,
                or as complex as applying cross-field validations.
            </Paragraph>
            <Code language={"java"}>
                {`
                        public final class Email {
                            public final EmailAddress sender;
                            public final EmailAddress receiver;
                            public final Subject subject;
                            public final Body body;

                            public static Email restore(final EmailAddress sender,
                                                        final EmailAddress receiver,
                                                        final Subject subject,
                                                        final Body body) {
                                RequiredParameterValidator.ensureNotNull(sender, "sender");
                                RequiredParameterValidator.ensureNotNull(receiver, "receiver");
                                RequiredParameterValidator.ensureNotNull(body, "body");
                                return new Email(sender, receiver, subject, body);
                        }
                `}
            </Code>
            <Paragraph>
                Unfortunately, modern (de)serialization and validation frameworks don't play well with this kind of
                DTOs.
            </Paragraph>
            <Paragraph>
                Here is an example of a JSON you would get in the best case if you feed an email DTO to such a
                framework with its default configuration:
            </Paragraph>
            <Code language={"json"}>
                {`
                    {
                      "sender": {
                        "value": "sender@example.com"
                      },
                      "receiver": {
                        "value": "receiver@example.com"
                      },
                      "subject": {
                        "value": "subject"
                      },
                      "body": {
                        "value": "body"
                      }
                    }
                `}
            </Code>
            <Paragraph>
                While what one would expect is:
            </Paragraph>
            <Code language={"json"}>
                {`
                    {
                      "sender": "sender@example.com",
                      "receiver": "receiver@example.com",
                      "subject": "subject",
                      "body": "body"
                    }
                `}
            </Code>
            <Paragraph>
                While this problem can be mitigated using a ton of boilerplate code, validation is a different kind
                of
                beast, which becomes deadly the moment you want to "report all validation errors at once" from your
                server.
                Why not tell the user right away that both sender and receiver are invalid, instead of sending her on
                a quest to obtain permit A38. In fact, that is how we felt when trying to write modern microservices
                while
                adhering to the best practices of the Clean Code, the "always valid" approach of Domain Driven Design,
                Domain Driven
                Security...
            </Paragraph>
            <Paragraph>
                And that is the problem MapMate is there to solve.
            </Paragraph>
            <SubHeaderText>MapMate</SubHeaderText>
            <Paragraph>
                As with<ExternalLink to={"/httpmate-intro"} displayText={"HttpMate"}/> we made sure to provide an
                easy
                builder to start with while keeping the possibility of fine grain customization. Here is the
                absolute
                minimum configuration that would make our Email example serialize, deserialize and validate our
                Custom
                Primitives and DTOs.<CollapsibleArea title={"technical details"}>
                The defaults assumed upon constructing this MapMate instance are:

                <ul>
                    <li>
                        A class is considered to be a Custom Primitive if it has a serialization method named
                        "stringValue"
                        and a static deserialization method named "fromStringValue".
                    </li>
                    <li>
                        A class is considered to be a Data Transfer Object if it has a public static factory
                        method name "deserialize".
                    </li>
                    <li>
                        DTOs are serialized using the public fields(key:value) and deserialized using the same
                        public
                        factory method that was used to determine the class being a DTO.
                    </li>
                    <li>
                        On exception, by default, you will receive an instance of
                        "UnrecognizedExceptionOccurredException"
                    </li>
                </ul>
            </CollapsibleArea>
            </Paragraph>
            <Code language={"java"}>
                {`
                        public static MapMate mapMate() {
                            return MapMate.aMapMate("com.envimate.examples.email_use_case")
                                    .usingJsonMarshallers(new Gson()::toJson, new Gson()::fromJson)
                                    .build();
                        }
                `}
            </Code>
            <Paragraph>
                This piece will make the following JSON a valid request:
            </Paragraph>
            <Code language={"json"}>
                {`
                    {
                        "sender": "sender@example.com",
                        "receiver": "receiver@example.com",
                        "subject": "Hello world!",
                        "body": "Hello from Sender to Receiver!"
                    }
                `}
            </Code>
            <Paragraph>
                You must specify the package to scan(recursively) and a pair of (un)marshallers. This can be
                anything
                that can make a string out of a Map and vice-versa.<CollapsibleArea title={"technical details"}>
                MapMate is focusing on providing seamless mapping of your domain model.
                We felt the world doesn't need yet another JSON/XML/YAML parser, hence MapMate delegates that work
                to
                the provided "(un)marshaller". It is, in fact, unaware of the underlying "format". Be that JSON,
                XML,
                YAML or RAML that you are communicating, MapMate will be able to give you a valid <ItalicText>Data
                Transfer Object</ItalicText> you can work with.
            </CollapsibleArea> Here's an example using ObjectMapper:
            </Paragraph>
            <Code language={"java"}>
                {`
                        final ObjectMapper objectMapper = new ObjectMapper();
                        return MapMate.aMapMate("com.envimate.examples.email_use_case")
                                .usingJsonMarshallers(value -> {
                                    try {
                                        return objectMapper.writeValueAsString(value);
                                    } catch (JsonProcessingException e) {
                                        throw new UnsupportedOperationException("Could not parse value " + value, e);
                                    }
                                }, new Unmarshaller() {
                                    @Override
                                    public <T> T unmarshal(final String input, final Class<T> type) {
                                        try {
                                            return objectMapper.readValue(input, type);
                                        } catch (final IOException e) {
                                            throw new UnsupportedOperationException("Could not parse value " + input + " to type " + type, e);
                                        }
                                    }
                                })
                                .withExceptionIndicatingValidationError(CustomTypeValidationException.class)
                                .build();
                `}
            </Code>
            <Paragraph>
                What about the promised validation exception aggregation?<br/>
                In our example, all of our validations return an instance of CustomTypeValidationException if the
                Custom Primitive or the DTO are not valid.
            </Paragraph>
            <Paragraph>
                Add the following line, to instruct MapMate to recognize your Exception class as an indication of a
                validation error.
            </Paragraph>
            <Code language={"java"}>
                {`
                        public static MapMate mapMate() {
                            return MapMate.aMapMate("com.envimate.examples.email_use_case")
                                    .usingJsonMarshallers(new Gson()::toJson, new Gson()::fromJson)
                                    .withExceptionIndicatingValidationError(CustomTypeValidationException.class)
                                    .build();
                        }
                `}
            </Code>
            <Paragraph>
                Now if we try the following request:
            </Paragraph>
            <Code language={"json"}>
                {`
                    {
                      "sender": "not-a-valid-sender-value",
                      "receiver": "not-a-valid-receiver-value",
                      "subject": "Hello world!",
                      "body": "Hello from Sender to Receiver!"
                    }
                `}
            </Code>
            <Paragraph>
                We will get the following response:
            </Paragraph>
            <Code language={"bash"}>
                {`
                HTTP/1.1 400 Bad Request
                Date: Tue, 04 Jun 2019 18:30:51 GMT
                Transfer-encoding: chunked

                {"message":"receiver: Invalid email address: \u0027not-a-valid-receiver-value\u0027,sender: Invalid email address: \u0027not-a-valid-sender-value\u0027"}
            `}
            </Code>
            <SubHeaderText>Final words</SubHeaderText>
            <Paragraph>
                The presented builder of MapMate is there to make the initial usage easy. However, all of the
                described defaults are configurable, furthermore you can exclude packages and classes both from the
                Custom Primitives and DTOs, you can configure which exceptions are considered Validation Errors and
                how
                they are handled, you can specify a different method name for the Custom Primitive serialization, or
                provide
                your lambda for (de)serialization of both altogether.
            </Paragraph>
            <Paragraph>
                For more examples and details about MapMate,
                take a look at the<ExternalLink to={"https://github.com/envimate/mapmate"}
                                                displayText={"MapMate repository"}/>.
            </Paragraph>
            <Paragraph>
                Let us know what you think and what features would you like to see in MapMate next!
            </Paragraph>
        </SummaryColumn>
};

export default MapMateIntro
